import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import classNames from 'classnames'
import { Chip, Dropdown, EmptyState, Icon, Scrollbar } from 'shared/ui'
import { DropdownContent } from 'shared/ui/Dropdown/ui/DropdownContent'
import { DropdownCard } from 'shared/ui/Dropdown/ui/DropdownCard/DropdownCard'
import { EnumDropdownItemVariant } from 'shared/ui/Dropdown/ui/types'
import { useCreditUsageSourcesContext } from 'pages/analytics/pages/creditUsage/pages/sources/context/creditUsageSourcesContext'
import styles from './styles.module.scss'

const MAX_TRIGGER_CHIPS = 3
const DEFAULT_ICON_COLOR = 'var(--content-primary-tertiary)'

export const SourceGroupFilter = observer(() => {
  const { sourceGroupFilterStore } = useCreditUsageSourcesContext()
  const {
    isAllActive,
    isAllApplied,
    appliedOptions,
    selectedOptions,
    availableOptions,
    select,
    remove,
    selectAll,
    apply,
  } = sourceGroupFilterStore

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const trimmedSearch = search.trim().toLowerCase()
  const filteredOptions = useMemo(() => {
    if (!trimmedSearch) return availableOptions

    return availableOptions.filter((option) => option.label.toLowerCase().includes(trimmedSearch))
  }, [availableOptions, trimmedSearch])
  const visibleTriggerOptions = appliedOptions.slice(0, MAX_TRIGGER_CHIPS)
  const hiddenTriggerCount = appliedOptions.length - visibleTriggerOptions.length

  return (
    <Dropdown
      ariaLabel={'CreditUsageSourceGroupFilter'}
      placement={'bottom-start'}
      width={'420px'}
      show={open}
      onToggle={(status) => {
        setOpen(status)
        if (!status) {
          setSearch('')
          apply()
        }
      }}
      triggerComponent={(isOpen) => (
        <div className={classNames(styles.trigger, { [styles.triggerActive]: isOpen })}>
          {isAllApplied ? (
            <>
              <Icon icon={'menu'} fontSize={16} tertiary />
              <span className={styles.triggerValue}>{'All'}</span>
            </>
          ) : (
            <div className={styles.triggerChips}>
              {visibleTriggerOptions.map((option) => (
                <Chip
                  key={option.id}
                  label={option.label}
                  iconL={
                    option.icon ? { icon: option.icon, iconColor: option.iconColor } : undefined
                  }
                />
              ))}
              {hiddenTriggerCount > 0 && (
                <span className={styles.triggerBadge}>{`+${hiddenTriggerCount}`}</span>
              )}
            </div>
          )}
          <Icon icon={'chevronDown'} fontSize={16} tertiary />
        </div>
      )}
      customComponent={() => (
        <DropdownContent noPadding className={styles.panel}>
          {selectedOptions.length > 0 && (
            <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={96}>
              <div className={styles.chips}>
                {selectedOptions.map((option) => (
                  <Chip
                    key={option.id}
                    label={option.label}
                    iconL={
                      option.icon ? { icon: option.icon, iconColor: option.iconColor } : undefined
                    }
                    iconType={'div'}
                    onDelete={() => remove(option.id)}
                  />
                ))}
              </div>
            </Scrollbar>
          )}

          <input
            type={'text'}
            className={styles.search}
            placeholder={'Search'}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          {!trimmedSearch && (
            <div className={styles.allRow}>
              <DropdownCard
                item={{
                  id: 'all',
                  label: 'All',
                  iconL: 'menu',
                  activeValue: isAllActive,
                  variant: EnumDropdownItemVariant.CheckedRight,
                }}
                onChange={selectAll}
              />
            </div>
          )}

          <div className={styles.list}>
            {filteredOptions.length > 0 ? (
              <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={480}>
                {filteredOptions.map((option) => (
                  <DropdownCard
                    key={option.id}
                    item={{
                      id: option.id,
                      label: option.label,
                      iconL: option.icon,
                      variant: EnumDropdownItemVariant.Default,
                      customFields: { leftIconColor: option.iconColor ?? DEFAULT_ICON_COLOR },
                    }}
                    onChange={() => {
                      select(option.id)
                      setSearch('')
                    }}
                  />
                ))}
              </Scrollbar>
            ) : (
              <EmptyState text='No results found' />
            )}
          </div>
        </DropdownContent>
      )}
    />
  )
})
